import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ILoginState } from '../store/login/login';
import { pathGenerate } from '@poste-click-connect/shared-ui';
import { LoginPayload } from '../interfaces/login-payload';
import { ResponseNotifications } from '../interfaces/response-notifications';
import { ResponseSurvey } from '../interfaces/response-survey';
import { ResponseRegexDhl } from '../interfaces/response-regex-dhl';
import { ResponseExpiredObjects } from '../interfaces/response-expired-objects';
import { Config } from '../interfaces/config';
import { ResponseStatoInserimento } from '../interfaces/response-stato-inserimento';
import {
  IPayloadMovimentazioneAccettazione,
  IPayloadMovimentazioneAccettazioneBXL,
  ResponseAccettazioneDatamatrix,
  ResponseConfirmClientAcceptance,
  ResponseConfirmCodiceBusta,
  ResponseEtichettaLottomatica,
  ResponseEtichettaWebApp,
} from '../interfaces/response-confirm-client-acceptance';
import { IParcelData } from '../interfaces/response-verify-delivery';
import {
  IPayloadRifiutaPacco,
  IResponseRifiutaPacco,
} from '../interfaces/rifiuta-pacco';
import { RetrievePasswordPayload } from '../interfaces/retrieve-password-payload';
import { ResponseRetrievePassword } from '../interfaces/response-retrieve-password';
import { ISaleData } from '../interfaces/response-verify-sale';
import {
  IParcelSale,
  IPayloadParcelSale,
} from '../interfaces/response-parcel-sale';
import {
  IPayloadPaccoSmarrito,
  IResponsePaccoSmarrito,
} from '../interfaces/pacco-smarrito';
import { IResponseRetrieveBolla } from '../interfaces/response-retrieve-bolla';
import {
  IPayloadEliminaMagazzinoPickup,
  IResponseMagazzinoPickup,
} from '../interfaces/response-magazzino-pickup';
import { IBollaPost, IBollaPostResponse } from '../interfaces/bolla-post';
import { IMagazzinoContatoriResponse } from '../interfaces/magazzino-contatori';
import {
  IPayloadCourierAcceptance,
  IResponseVerificaCodeBolleList,
  IResponseVerificaCodeSpedizioniList,
} from '../interfaces/courier-acceptance';
import {
  IParamsStampaScontrinoRitiro,
  IStampaScontrinoRitiroResponse,
} from '../interfaces/stampa-scontrino-ritiro';
import {
  IAcquisisciBollaPayload,
  IAcquisisciBollaResponse,
  IAnnullaBolla,
} from '../interfaces/acquisisci-bolla';
import { IPacchiInGiacenzaResponse } from '../interfaces/response-pacchi-giacenza';
import {
  ICollectPointPayload,
  IMovimentazioneListPayload,
  IMovimentazioneListResponse,
} from '../interfaces/movimentazione-list';
import { IMovimentazioneMailResponse } from '../interfaces/movimentazione-mail';
import {
  IPayloadMagazzinoPacchi,
  IVerificaMagazzinoPacchi,
} from '../interfaces/response-verifica-magazzino-pacchi';
import { IBeniContoDeposito } from '../interfaces/beni-conto-deposito';
import {
  IPayloadScatolaNonDisponibile,
  IResponseScatolaNonDisponibile,
} from '../interfaces/scatola-non-disponibile';
import {
  IPayloadChangeStatusVerifica,
  IResponseChangeStatusVerifica,
} from '../interfaces/change-status-verifica';
import { IResponseExistsScatolaFlyer } from '../interfaces/exists-scatola-flyer';
import {
  IPayloadChiudiScatola,
  IResponseChiudiScatola,
} from '../interfaces/chiudi-scatola';
import {
  IPayloadChiusuraScatoleBxl,
  IResponseChiusuraScatoleBxl,
} from '../interfaces/chiusura-scatole-bxl';
import {
  IPayloadStoricoAnnualeContoDeposito,
  IPayloadStoricoOggettoContoDeposito,
  IResponseStoricoOggettoContoDeposito,
} from '../interfaces/storico-oggetto-conto-deposito';
import {
  ICatalogoAppartenenzaResoResponse,
  ICatalogoAppartenenzaResponse,
  IProdottiResoResponse,
  IProductsBoxAcceptance,
} from '../interfaces/catalogo-appartenenza';
import {
  IPayloadApprovvigionamentoOrdine,
  IPayloadApprovvigionamentoOrdineFinale,
  IResponseApprovvigionamentoOrdine,
  IResponseApprovvigionamentoOrdineFinale,
  IResponseApprovvigionamentoProdotti,
} from '../interfaces/approvvigionamento-ordine';
import { IdReturnsActionDropdownEnum } from '../enums/consumable-warehouse.enum';
import {
  IPayloadGenerateBarcodeReso,
  IResponseGenerateBarcodeReso,
} from '../interfaces/generate-barcode-scatola-reso';
import { IResponseAnagrafica } from '../interfaces/anagrafica';
import { IResponseCreaReso } from '../interfaces/crea-reso';
import {
  IPayloadCambioPassword,
  IResponseCambioPassword,
} from '../interfaces/cambio-password';
import {
  IPayloadPutOrari,
  IResponseOrariChiusure,
} from '../interfaces/orari-chiusure';
import {
  IPayloadStoricoOrdiniMateriali,
  IResponseStoricoOrdiniMateriali,
} from '../interfaces/storico-ordini-materiali';
import { IPayloadBacheca, IResponseBacheca } from '../interfaces/bacheca';
import {
  IPayloadMovimentazioneCreateEvent,
  IResponseMovimentazioneCreateEvent,
} from '../interfaces/movimentazione-create-event';
import {
  IPayloadVerificaAccettazioneStampaPaperless,
  IResponseVerificaAccettazioneStampaPaperless,
} from '../interfaces/verifica-accettazione-stampa-paperless';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private basepath: string = '';
  private staticBasepath: string = '';
  private isLocal: boolean = true;

  constructor(private http: HttpClient) {}

  retrieveConfig(): Observable<Config> {
    return this.http.get<Config>('assets/config/config.json').pipe(
      tap((url: Config) => {
        this.basepath = url.basepath;
        this.staticBasepath = url.staticBasepath;
        this.isLocal = url.isLocal;
      })
    );
  }

  login(body: LoginPayload): Observable<ILoginState> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/listaAccessi/login'
    );

    return this.http.put<ILoginState>(path, body);
  }

  retrievePassword(
    body: RetrievePasswordPayload
  ): Observable<ResponseRetrievePassword> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/listaAccessi/recuperapassword'
    );

    return this.http.put<ResponseRetrievePassword>(path, body);
  }

  retrieveNotifications(): Observable<ResponseNotifications> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/comunicazione/notifiche'
    );

    return this.http.get<ResponseNotifications>(path);
  }

  retrieveSurvey(): Observable<ResponseSurvey> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/cp/datiAscoltoRetiTerze'
    );

    return this.http.get<ResponseSurvey>(path);
  }

  retrieveRegexDhl(): Observable<ResponseRegexDhl> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/magazzino/getRegexCodiceDhlInbound'
    );

    return this.http.get<ResponseRegexDhl>(path);
  }

  retrieveExpiredObjects(): Observable<ResponseExpiredObjects> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/magazzino/checkOggettiScadutiBXL'
    );

    return this.http.get<ResponseExpiredObjects>(path);
  }

  retrieveIsAbleToDigitCode(): Observable<ResponseStatoInserimento> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/movimentazione/checkStatoInserimentoCodicePacco'
    );
    return this.http.get<ResponseStatoInserimento>(path);
  }

  retrieveVerifyDelivery(pin: string): Observable<IParcelData> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/consegna/${pin}`
    );

    return this.http.get<IParcelData>(path);
  }

  retrieveVerifyDeliveryNoPin(parcelCode: string): Observable<IParcelData> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/consegna/senza-pin/${parcelCode}`
    );

    return this.http.get<IParcelData>(path);
  }

  retrieveRifiutaPacco(
    payload: IPayloadRifiutaPacco
  ): Observable<IResponseRifiutaPacco> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/pacco'
    );

    return this.http.post<IResponseRifiutaPacco>(path, payload);
  }

  verificaAccettazioneStampaPaperless(
    payload: IPayloadVerificaAccettazioneStampaPaperless
  ): Observable<IResponseVerificaAccettazioneStampaPaperless> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/accettazione/stampa/${payload.barcode}/${payload.origine}`
    );

    return this.http.post<IResponseVerificaAccettazioneStampaPaperless>(
      path,
      payload
    );
  }

  retrieveConsegnaPacco(
    payload: IPayloadRifiutaPacco
  ): Observable<IResponseRifiutaPacco> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/movimentazione/consegna'
    );

    return this.http.put<IResponseRifiutaPacco>(path, payload);
  }

  retrievePaccoSmarrito(
    payload: IPayloadPaccoSmarrito
  ): Observable<IResponsePaccoSmarrito> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/pacco'
    );

    return this.http.post<IResponsePaccoSmarrito>(path, payload);
  }

  retrieveVerifySale(code: string): Observable<ISaleData> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/vendita/${code}`
    );

    return this.http.get<ISaleData>(path);
  }

  retrieveParcelSale(payload: IPayloadParcelSale): Observable<IParcelSale> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/movimentazione/vendita'
    );

    return this.http.put<ISaleData>(path, payload);
  }
  retrieveConfirmClientAcceptance(
    boxCode: string
  ): Observable<ResponseConfirmClientAcceptance> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/accettazione/${boxCode}`
    );
    return this.http.get<ResponseConfirmClientAcceptance>(path);
  }

  retrieveConfirmClientAcceptanceDatamatrix(
    dataMatrix: string
  ): Observable<ResponseAccettazioneDatamatrix> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/accettazione`
    );

    const payload = {
      spedizioni: [{ dataMatrix }],
    };

    return this.http.post<ResponseAccettazioneDatamatrix>(path, payload);
  }

  retriveEtichettaBXLwebApp(): Observable<ResponseEtichettaWebApp> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/stampaEtichetteBXL`
    );

    return this.http.get<ResponseEtichettaWebApp>(path);
  }

  retriveEtichettaBXLlottomatica(): Observable<ResponseEtichettaLottomatica> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/stampaEtichetteLottomaticaBXL`
    );

    return this.http.get<ResponseEtichettaLottomatica>(path);
  }

  verificaCodiceBustaBXL(
    codiceBusta: string
  ): Observable<ResponseConfirmCodiceBusta> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/verificaAccettazioneBXL/${codiceBusta}?codiceServizio=BXL`
    );

    return this.http.get<ResponseConfirmCodiceBusta>(path);
  }

  concludiAccettazione(
    payload: IPayloadMovimentazioneAccettazione
  ): Observable<{ esito: string; msgCode: string }> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/accettazione`
    );

    return this.http.put<{ esito: string; msgCode: string }>(path, payload);
  }

  concludiAccettazioneBXL(
    payload: IPayloadMovimentazioneAccettazioneBXL
  ): Observable<{ esito: string }> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/accettazioneBXL`
    );

    return this.http.put<{ esito: string }>(path, payload);
  }

  retrieveBolla(): Observable<IResponseRetrieveBolla> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/bolla`
    );

    return this.http.get<IResponseRetrieveBolla>(path);
  }

  retrieveMagazzinoPickup(): Observable<IResponseMagazzinoPickup> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/magazzino/pickup`
    );

    return this.http.get<IResponseMagazzinoPickup>(path);
  }

  retrieveBollaPost(
    payloadBollaPost: IBollaPost
  ): Observable<IBollaPostResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/bolla`
    );

    return this.http.post<IBollaPostResponse>(path, payloadBollaPost);
  }

  retrieveMagazzinoContatori(): Observable<IMagazzinoContatoriResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/magazzino/contatori`
    );

    return this.http.get<IMagazzinoContatoriResponse>(path);
  }

  verificaCodicePaccoCodiceAggregazione(
    boxCode: string
  ): Observable<
    IResponseVerificaCodeBolleList | IResponseVerificaCodeSpedizioniList
  > {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/dropoff/${boxCode}`
    );
    return this.http.get<
      IResponseVerificaCodeBolleList | IResponseVerificaCodeSpedizioniList
    >(path);
  }

  retrieveStampaScontrinoRitiro(
    params: IParamsStampaScontrinoRitiro
  ): Observable<IStampaScontrinoRitiroResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/verifica/stampaScontrinoRitiro`
    );

    const paramsObj: Record<string, any> = params;
    const queryParams = new HttpParams().appendAll(paramsObj);

    return this.http.get<IStampaScontrinoRitiroResponse>(path, {
      params: queryParams,
    });
  }

  retrieveAcquisisciBolla(
    payload: IAcquisisciBollaPayload
  ): Observable<IAcquisisciBollaResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/bolla/acquisisciBollaPrePalmare`
    );

    return this.http.put<IAcquisisciBollaResponse>(path, payload);
  }

  confermaCourierAcceptance(
    payload: IPayloadCourierAcceptance
  ): Observable<{ esito: string; msgCode?: string }> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/dropoff`
    );

    return this.http.put<IAcquisisciBollaResponse>(path, payload);
  }

  retrieveMagazzinoPickupDelete(
    payload: IPayloadEliminaMagazzinoPickup
  ): Observable<IResponseMagazzinoPickup> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/magazzino/pickup`
    );

    return this.http.put<IResponseMagazzinoPickup>(path, payload);
  }

  retrieveAnnullaBolla(): Observable<IAnnullaBolla> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/bolla`
    );

    return this.http.delete<IAnnullaBolla>(path);
  }

  retrieveConsegnaSenzaPalmare(): Observable<IAnnullaBolla> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/pickup`
    );

    return this.http.put<IAnnullaBolla>(path, {});
  }

  retrievePacchiGiacenza(): Observable<IPacchiInGiacenzaResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/magazzino`
    );

    return this.http.get<IPacchiInGiacenzaResponse>(path);
  }

  retrieveMovimentazioneList(
    payload: IMovimentazioneListPayload
  ): Observable<IMovimentazioneListResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/list`
    );

    return this.http.post<IMovimentazioneListResponse>(path, payload);
  }

  retrieveEsportaXls(payload: IMovimentazioneListPayload): Observable<any> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/list/xls`
    );

    const options: {
      headers?: HttpHeaders;
      observe?: 'body';
      params?: HttpParams;
      reportProgress?: boolean;
      responseType: 'json';
      withCredentials?: boolean;
    } = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.http.put<any>(path, payload, options);
  }

  retrieveEsportaMail(
    payload: ICollectPointPayload
  ): Observable<IMovimentazioneMailResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/list/mail`
    );

    return this.http.put<IMovimentazioneMailResponse>(path, payload);
  }

  retriveVerificaMagazzinoPacchi(): Observable<IVerificaMagazzinoPacchi> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/magazzino/verificare'
    );

    return this.http.get<IVerificaMagazzinoPacchi>(path);
  }

  retriveEditMagazzinoPacchi(
    payload: IPayloadMagazzinoPacchi
  ): Observable<IVerificaMagazzinoPacchi> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/magazzino/verificare'
    );

    return this.http.post<IVerificaMagazzinoPacchi>(path, payload);
  }

  retrieveBeniContoDeposito(): Observable<IBeniContoDeposito> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/magazzino/oggettoContoDeposito`
    );

    return this.http.get<IBeniContoDeposito>(path);
  }

  retrieveScatolaNonDisponibile(
    payload: IPayloadScatolaNonDisponibile
  ): Observable<IResponseScatolaNonDisponibile> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/scatolaBXLNonDisponibile`
    );

    return this.http.put<IResponseScatolaNonDisponibile>(path, payload);
  }

  retrieveChangeStatusVerifica(
    payload: IPayloadChangeStatusVerifica
  ): Observable<IResponseChangeStatusVerifica> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/changeStatusInVerifica`
    );

    return this.http.put<IResponseChangeStatusVerifica>(path, payload);
  }

  retrieveExistsScatolaFlyer(
    code: string
  ): Observable<IResponseExistsScatolaFlyer> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/existsScatolaForFlayer/${code}`
    );

    return this.http.get<IResponseExistsScatolaFlyer>(path);
  }

  retrieveChiudiScatola(
    payload: IPayloadChiudiScatola
  ): Observable<IResponseChiudiScatola> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/generateBarcodeScatolaBXL`
    );

    return this.http.put<IResponseChiudiScatola>(path, payload);
  }

  retrieveChiusuraScatoleBxl(
    payload: IPayloadChiusuraScatoleBxl
  ): Observable<IResponseChiusuraScatoleBxl> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/chiusuraScatoleBXL`
    );

    return this.http.put<IResponseChiusuraScatoleBxl>(path, payload);
  }

  retrieveStoricoContoDeposito(
    payload: IPayloadStoricoOggettoContoDeposito
  ): Observable<IResponseStoricoOggettoContoDeposito> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/magazzino/storicoOggettoContoDeposito`
    );

    return this.http.put<IResponseStoricoOggettoContoDeposito>(path, payload);
  }

  retrieveStoricoAnnualeContoDeposito(
    payload: IPayloadStoricoAnnualeContoDeposito
  ): Observable<any> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/storicoAnnualeContoDeposito/xls`
    );

    const options: {
      headers?: HttpHeaders;
      observe?: 'body';
      params?: HttpParams;
      reportProgress?: boolean;
      responseType: 'json';
      withCredentials?: boolean;
    } = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.http.put<any>(path, payload, options);
  }

  retrieveCatalogoAppartenenza(): Observable<ICatalogoAppartenenzaResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/catalogoAppartenenza`
    );

    return this.http.get<ICatalogoAppartenenzaResponse>(path);
  }

  retrieveApprovvigionamentoOrdine(
    payload: IPayloadApprovvigionamentoOrdine
  ): Observable<IResponseApprovvigionamentoOrdine> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/ordine/frequenza`
    );

    return this.http.post<IResponseApprovvigionamentoOrdine>(path, payload);
  }

  retrieveApprovvigionamentoProdotti(
    payload: IPayloadApprovvigionamentoOrdine
  ): Observable<IResponseApprovvigionamentoProdotti> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/prodotti`
    );

    return this.http.post<IResponseApprovvigionamentoProdotti>(path, payload);
  }

  retrieveApprovvigionamentoOrdineFinale(
    payload: IPayloadApprovvigionamentoOrdineFinale
  ): Observable<IResponseApprovvigionamentoOrdineFinale> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/ordine`
    );

    return this.http.post<IResponseApprovvigionamentoOrdineFinale>(
      path,
      payload
    );
  }

  retrieveCatalogoAppartenenzaReso(): Observable<ICatalogoAppartenenzaResoResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/catalogoAppartenenzaReso`
    );

    return this.http.get<ICatalogoAppartenenzaResoResponse>(path);
  }

  retrieveProdottiReso(
    returnType: IdReturnsActionDropdownEnum
  ): Observable<IProdottiResoResponse> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/prodottiReso/${returnType}`
    );

    return this.http.get<IProdottiResoResponse>(path);
  }

  retrieveProductsBoxAcceptance(): Observable<IProductsBoxAcceptance> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/prodottiScatolaAccettazione`
    );

    return this.http.get<IProductsBoxAcceptance>(path);
  }

  retrieveGenerateBarcodeReso(
    payload: IPayloadGenerateBarcodeReso
  ): Observable<IResponseGenerateBarcodeReso> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/generateBarcodeScatolaReso`
    );

    return this.http.post<IResponseGenerateBarcodeReso>(path, payload);
  }

  retrieveCreaReso(
    payload: IPayloadGenerateBarcodeReso
  ): Observable<IResponseCreaReso> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/creaReso`
    );

    return this.http.post<IResponseCreaReso>(path, payload);
  }

  retrieveStoricoOrdiniMateriali(
    payload: IPayloadStoricoOrdiniMateriali
  ): Observable<IResponseStoricoOrdiniMateriali> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/storico`
    );

    return this.http.post<IResponseStoricoOrdiniMateriali>(path, payload);
  }

  retrieveEsportaStoricoOrdini(
    payload: IPayloadStoricoOrdiniMateriali
  ): Observable<any> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/storico/xls`
    );

    const options: {
      headers?: HttpHeaders;
      observe?: 'body';
      params?: HttpParams;
      reportProgress?: boolean;
      responseType: 'json';
      withCredentials?: boolean;
    } = {
      responseType: 'arraybuffer' as 'json',
    };

    return this.http.post<any>(path, payload, options);
  }

  retrieveAnagrafica(): Observable<IResponseAnagrafica> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/cp`
    );

    return this.http.get<IResponseAnagrafica>(path);
  }

  retrieveCambioPassword(
    payload: IPayloadCambioPassword
  ): Observable<IResponseCambioPassword> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/listaAccessi/cambiopassword`
    );

    return this.http.put<IResponseCambioPassword>(path, payload);
  }

  retrieveOrariChiusure(): Observable<IResponseOrariChiusure> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/cp/aperture-chiusure`
    );

    return this.http.get<IResponseOrariChiusure>(path);
  }

  retrievePutOrariChiusure(
    payload: IPayloadPutOrari
  ): Observable<IResponseOrariChiusure> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/cp/aperture-chiusure`
    );

    return this.http.put<IResponseOrariChiusure>(path, payload);
  }

  retrieveBacheca(payload: IPayloadBacheca): Observable<IResponseBacheca> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/comunicazione/bacheca'
    );

    return this.http.post<IResponseBacheca>(path, payload);
  }

  retrieveDettaglioBacheca(idBacheca: number): Observable<IResponseBacheca> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/comunicazione/bacheca/${idBacheca}`
    );

    return this.http.get<IResponseBacheca>(path);
  }

  retrieveMailStoricoOrdiniMateriali(
    payload: IPayloadStoricoOrdiniMateriali
  ): Observable<IResponseStoricoOrdiniMateriali> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/approvvigionamento/storico/mail`
    );

    return this.http.post<IResponseStoricoOrdiniMateriali>(path, payload);
  }

  retrieveMovimentazioneCreateEvent(
    payload: IPayloadMovimentazioneCreateEvent
  ): Observable<IResponseMovimentazioneCreateEvent> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      `/ClickECollectFO-BE/movimentazione/createEvent`
    );

    return this.http.put<IResponseMovimentazioneCreateEvent>(path, payload);
  }

  retriveLogout(): Observable<{ esito: string }> {
    const path = pathGenerate(
      this.basepath,
      this.staticBasepath,
      this.isLocal,
      '/ClickECollectFO-BE/listaAccessi/logout'
    );

    return this.http.put<{ esito: string }>(path, null);
  }
}
